// Phase 43 — <ProvenanceChip /> (Plan 43-04 Task 2, CANVAS-03).
// Label-row chip primitive: per-source icon + confidence %. Click opens
// Radix Popover hosting <ProvenancePopover>. Typography UI-SPEC rev 2 locked:
// Size lock: 13px at weight 400 (text-[0.8125rem] font-normal). The 12px
// utility and weight-500 class names are disallowed by UI-SPEC rev 2.
//
// aria-label exact string per Plan 43-04 must_haves:
//   "{source} source, {confidence}% confidence. Click for details."
// (React auto-escapes — T-43-15 mitigation.)

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@marine/ui/popover';
import {
  Sparkles,
  AlertTriangle,
  Info,
  FileText,
  Fingerprint,
  Camera,
  Check,
  Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProvenancePopover } from './ProvenancePopover';
import type {
  ProvenanceEntry,
  ProvenanceSource,
} from '@/modules/inventory/types/boat.types';

interface ProvenanceChipProps {
  fieldName: string;
  entry: ProvenanceEntry | ProvenanceEntry[] | null;
  onClear?: () => void;
  onChangeSource?: () => void;
  onRestoreAiValue?: () => void;
  /**
   * Phase 45.5 (UI-SPEC §"AI-filled field states") — when true, this single AI
   * fill is low-confidence/scanned and renders amber + "please check" instead of
   * the violet "✦ {source} · {confidence}%". Driven by useProvenance's
   * `isLowConfidence` (field_confidence medium/low or a failed HIN/VIN regex).
   */
  isLowConfidence?: boolean;
  /**
   * Phase 45.5 (UI-SPEC §HIN-verified, D-02) — when true, render the emerald
   * "HIN verified" chip on the HIN field (the post-parse 12-char cross-check
   * passed). Distinct from the violet provenance chip.
   */
  isHinVerified?: boolean;
}

// Per UI-SPEC §"Copywriting Contract" → "Provenance chip" — source ∈
// {Boat COO, Trailer COO, BOL, Invoice, Photo, Web}. Phase 45.5 (D-01): the
// 'URL' source is retired (INGEST-02 cut); the 4 doc-typed packet sources are
// added with their verbatim human labels.
const SOURCE_LABEL: Record<ProvenanceSource, string> = {
  AI: 'AI',
  'AI-photo': 'Photo',
  PDF: 'PDF',
  VIN: 'VIN',
  manual: '', // never rendered — manual source has no chip
  'COO-boat': 'Boat COO',
  'COO-trailer': 'Trailer COO',
  BOL: 'BOL',
  invoice: 'Invoice',
  web: 'Catalogue',
};

/** The chip/chooser display name of a provenance source; a source without a label shows as itself. */
export function sourceLabel(source: ProvenanceSource): string {
  return SOURCE_LABEL[source] || source;
}

// The AI / packet sources render in violet (the reserved AI signal, UI-SPEC
// §Color). The boat-COO / trailer-COO / BOL / invoice doc sources ARE the AI
// reading a document → violet, with a doc glyph.
const VIOLET_SOURCES: ReadonlySet<ProvenanceSource> = new Set<ProvenanceSource>([
  'AI',
  'AI-photo',
  'COO-boat',
  'COO-trailer',
  'BOL',
  'invoice',
  'web',
]);

function getSourceIcon(source: ProvenanceSource) {
  switch (source) {
    case 'AI':
      return (
        <Sparkles
          className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
          role="img"
        />
      );
    case 'AI-photo':
      return (
        <Camera
          className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
          role="img"
        />
      );
    case 'COO-boat':
    case 'COO-trailer':
    case 'BOL':
    case 'invoice':
      // The AI read a packet document → violet doc glyph (AI signal).
      return (
        <FileText
          className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
          role="img"
        />
      );
    case 'web':
      return (
        <Globe
          className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
          role="img"
        />
      );
    case 'PDF':
      return (
        <FileText
          className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400"
          role="img"
        />
      );
    case 'VIN':
      return (
        <Fingerprint
          className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400"
          role="img"
        />
      );
    default:
      return null;
  }
}

export function ProvenanceChip({
  fieldName,
  entry,
  onClear,
  onChangeSource,
  onRestoreAiValue,
  isLowConfidence = false,
  isHinVerified = false,
}: ProvenanceChipProps) {
  // HIN-verified emerald chip (UI-SPEC §HIN-verified, D-02) — distinct from the
  // violet provenance chip; can render even when there's a provenance entry.
  const hinVerifiedChip = isHinVerified ? (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5',
        'text-[0.8125rem] font-normal',
        'text-emerald-700 dark:text-emerald-400',
      )}
      aria-label={`${fieldName} verified`}
    >
      <Check className="h-3.5 w-3.5" role="img" />
      <span>HIN verified</span>
    </span>
  ) : null;

  if (!entry) return hinVerifiedChip;

  // #1317 — a conflict the dealer resolved in the chooser is attributed to the
  // picked (confirmed) source: it renders that source's normal chip.
  const picked = Array.isArray(entry) ? (entry.find((e) => e.state === 'confirmed') ?? null) : null;
  const isConflict = Array.isArray(entry) && picked === null;
  if (isConflict) {
    // UI-SPEC §"AI-filled field states" — conflict affordance reads
    // "Sources disagree · review" (verbatim). Clicking the field's
    // ConflictChooser (rendered in place by CanvasFormSections) resolves it.
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5',
          // 13px font-normal — UI-SPEC rev 2 lock
          'text-[0.8125rem] font-normal',
          'text-amber-800 dark:text-amber-200',
        )}
        aria-label={`Sources disagree for ${fieldName} — review the chooser above the field.`}
      >
        <AlertTriangle className="h-3.5 w-3.5" role="img" />
        <span>Sources disagree · review</span>
      </span>
    );
  }

  const single = picked ?? (entry as ProvenanceEntry);

  // User-entered (edited) — the chip is REPLACED by "✓ Edited by you" (UI-SPEC).
  if (single.userEdited) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5',
          'text-[0.8125rem] font-normal',
          'text-muted-foreground',
        )}
        aria-label={`${fieldName} edited by you`}
      >
        <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" role="img" />
        <span>Edited by you</span>
      </span>
    );
  }

  const pct = Math.round(single.confidence * 100);
  const label = SOURCE_LABEL[single.source];
  const isViolet = VIOLET_SOURCES.has(single.source);
  // Exact aria-label format locked by Plan 43-04 must_haves.
  const ariaLabel = `${single.source} source, ${pct}% confidence. Click for details.`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5',
            // 13px font-normal — UI-SPEC rev 2 lock (do NOT bump)
            'text-[0.8125rem] font-normal',
            // Low-confidence → amber "please check"; high-confidence AI/packet
            // → violet (the reserved AI signal); other sources → neutral.
            isLowConfidence
              ? 'text-amber-800 dark:text-amber-200'
              : isViolet
                ? 'text-violet-700 dark:text-violet-300'
                : 'text-foreground',
          )}
        >
          {isLowConfidence ? (
            <AlertTriangle
              className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400"
              role="img"
            />
          ) : (
            getSourceIcon(single.source)
          )}
          <span>
            {/* UI-SPEC §"Provenance chip": "✦ {source} · {confidence}%". A
                low-confidence fill appends the verbatim "please check" flag. */}
            ✦ {label} · {pct}%{isLowConfidence ? ' · please check' : ''}
          </span>
          <Info
            className="h-3 w-3 text-muted-foreground"
            role="img"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-[240px]">
        <ProvenancePopover
          entry={single}
          onClear={onClear}
          onChangeSource={onChangeSource}
          onRestoreAiValue={onRestoreAiValue}
        />
      </PopoverContent>
    </Popover>
  );
}
